import React, { Component } from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import { parseFullName } from 'parse-full-name';

import { Row, Col, Input, Form, Button, Typography } from 'antd';

import inboxRsc from '../../reducers/inbox/resources';
import { getSequenceTemplates } from '../inbox-v2/resources';
import { getContactByMobile, checkCanSendNumber } from '../../reducers/contact/resources';
import { checkUserGroupDefault } from '../../reducers/apps/resources';
import { getAssignedBots } from '../../reducers/bots/resources';
import session from '../../utils/session';

import PhoneInputUS from '../common/PhoneInputUS';
import MessageInputField from '../inbox-v2/components/MessageInputField/MessageInputField';

const { Title } = Typography;

class SendMessage extends Component {
	constructor(props) {
		super(props);
		this.state = {
			tmpls: [],
			seqTmpls: [],
			tonumber: '',
			firstname: '',
			lastname: '',
			cansend: 'askinput',
			mobileStatus: { status: '', help: null },
			isContactExists: false,
		};
		this.debouncedValidatePhone = _.debounce(this.validatePhone, 800);
	}

	componentDidMount() {
		(async () => {
			const [templates, seqRes, userBots] = await Promise.all([
				inboxRsc.getTemplates(),
				getSequenceTemplates(),
				// Refresh the session's cached bot list: after onboarding the
				// login-time userData.bots is stale and won't include the freshly
				// assigned 1-1 longcode, so the composer reports no texting number.
				getAssignedBots().catch(() => null),
			]);
			const seqData = seqRes?.data || seqRes;
			if (userBots?.data) {
				const userData = session.get('userData') || {};
				userData.bots = userBots.data;
				session.set('userData', userData);
			}
			this.setState({ tmpls: templates.data, seqTmpls: seqData?.rows || [] });
		})();
	}

	componentWillUnmount() {
		this.debouncedValidatePhone.cancel();
	}

	toNextPage = () => {
		this.props.history.replace('/inbox');
	};

	handleNameChange = (field) => (event) => {
		this.setState({ [field]: event.target.value });
	};

	handlePhoneChange = (value) => {
		let mobileNumber = '';
		if (typeof value === 'object' && value !== null) {
			const { countryCode, areaCode, phoneNumber } = value;
			if (areaCode && phoneNumber) {
				mobileNumber = `+${countryCode || 1}${areaCode || ''}${phoneNumber || ''}`;
			}
		} else if (typeof value === 'string') {
			mobileNumber = value;
		}

		this.setState({
			tonumber: mobileNumber,
			cansend: 'askinput',
			isContactExists: false,
			firstname: this.state.isContactExists ? '' : this.state.firstname,
			lastname: this.state.isContactExists ? '' : this.state.lastname,
			mobileStatus: { status: '', help: null },
		});

		if (mobileNumber.length >= 10) {
			this.debouncedValidatePhone(mobileNumber);
		} else {
			this.debouncedValidatePhone.cancel();
		}
	};

	validatePhone = async (mobileNumber) => {
		this.setState({
			cansend: 'checking',
			mobileStatus: { status: 'validating', help: 'Checking number...' },
		});

		try {
			const cansend = await checkCanSendNumber(mobileNumber);
			const contact = await getContactByMobile(mobileNumber);

			const data = { cansend: cansend.data.can_send };

			if (contact.data.length) {
				if (contact.data[0].contactDetails.length) {
					const contactDetails = contact.data[0].contactDetails;
					data.isContactExists = true;
					const firstname = _.find(contactDetails, { field: 'firstname' });
					const lastname = _.find(contactDetails, { field: 'lastname' });
					data.firstname = firstname ? firstname.value : this.state.firstname;
					data.lastname = lastname ? lastname.value : this.state.lastname;
				} else {
					const name = parseFullName(contact.data[0].name);
					data.isContactExists = true;
					data.firstname = name.first;
					data.lastname = name.last;
				}
			}

			if (data.cansend) {
				this.setState({ ...data, mobileStatus: { status: 'success', help: null } });
			} else {
				this.setState({
					...data,
					mobileStatus: { status: 'error', help: 'We cannot send to this number.' },
				});
			}
		} catch {
			this.setState({
				cansend: false,
				mobileStatus: { status: 'error', help: 'Error checking number.' },
			});
		}
	};

	render() {
		const { firstname, lastname, tonumber, cansend, mobileStatus, isContactExists } = this.state;

		return (
			<div style={{ width: 600, maxWidth: '100%', margin: '0 auto', padding: '24px 16px' }}>
				<Title level={2} style={{ marginBottom: 24 }}>Send your first SMS</Title>
				<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
					<Form>
						<Form.Item
							name="phone"
							label={<span style={{ fontWeight: 500 }}>Phone Number</span>}
							validateStatus={mobileStatus.status}
							help={mobileStatus.help}
							style={{ marginBottom: 0 }}
						>
							<PhoneInputUS
								enableSearch
								disabled={cansend === 'checking'}
								onChange={this.handlePhoneChange}
							/>
						</Form.Item>
					</Form>
					<Row style={{ display: 'flex', flexWrap: 'wrap' }}>
						<Col sm={6} style={{ flex: 1, marginRight: 16 }}>
							<div style={{ marginBottom: 4, fontWeight: 500 }}>First Name</div>
							<Input
								value={firstname}
								onChange={this.handleNameChange('firstname')}
								disabled={isContactExists}
								autoComplete="off"
								placeholder="First name"
							/>
						</Col>
						<Col sm={6} style={{ flex: 1 }}>
							<div style={{ marginBottom: 4, fontWeight: 500 }}>Last Name</div>
							<Input
								value={lastname}
								onChange={this.handleNameChange('lastname')}
								disabled={isContactExists}
								autoComplete="off"
								placeholder="Last name"
							/>
						</Col>
					</Row>
					<MessageInputField
						onCalendarSetup={() => {
							this.props.history.push('/account-setup/integrate-calendar');
						}}
						activeContactModel={cansend === true ? {
							id: 'new-message',
							name: `${firstname} ${lastname}`.trim() || 'Unknown',
							mobile: tonumber,
							blacklist: false,
						} : null}
						sendMessage={async (messageText, _botmapId, _contactId, _mobile, moduleGroup, translation) => {
							const data = {
								to: tonumber,
								body: messageText,
								firstname: firstname || 'unknown',
								lastname: lastname || 'unknown',
								sendType: 'new-message',
								...(moduleGroup ? { moduleGroup } : {}),
								...(translation ? { translation } : {}), // ADR-035
							};
							await inboxRsc.sendReply(data);
							this.toNextPage();
						}}
						userMessageTemplates={this.state.tmpls}
						userSequenceTemplates={this.state.seqTmpls}
						userHasCalendar={async () => {
							try {
								const data = await checkUserGroupDefault('calendar');
								return data.data;
							} catch {
								return [];
							}
						}}
						sendCalendarInvite={async (_mobile, _contactId, customMessage, schedulingUserId, translation) => {
							await inboxRsc.sendInterviewScheduling({
								to: tonumber,
								firstname: firstname || 'unknown',
								lastname: lastname || 'unknown',
								// Forward the composed text + chosen teammate's calendar
								// (MessageInputField passes these as the 3rd/4th args).
								...(customMessage ? { customMessage } : {}),
								...(schedulingUserId ? { schedulingUserId } : {}),
								...(translation ? { translation } : {}), // ADR-035
							});
							this.toNextPage();
						}}
					/>
					<div style={{ textAlign: 'right' }}>
						<Button onClick={this.toNextPage}>Skip for now</Button>
					</div>
				</div>
			</div>
		);
	}
}

export default connect()(SendMessage);
